<template>
  <div class="space-y



">
    <!-- Survey Schema Form Rendering -->
    <div v-if="isSurveySchema" class="space-y-2">
      <!-- Survey Title and Description -->
      <div v-if="workType.configuration.title || workType.configuration.description"
           class="bg-primary-50 rounded p-2.5">
        <h3 v-if="workType.configuration.title" class="text-xs font-semibold text-gray-900">
          {{ workType.configuration.title }}
        </h3>
        <p v-if="workType.configuration.description" class="text-xs text-gray-400 mt-0.5">
          {{ workType.configuration.description }}
        </p>
      </div>

      <!-- Progress Bar -->
      <div v-if="surveyFields.length > 1" class="bg-gray-50 rounded p-2">
        <div class="flex justify-between text-xs text-gray-600 mb-2">
          <span>Progress</span>
          <span>{{ completedFieldsCount }} of {{ requiredFieldsCount }} required fields completed</span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-2">
          <div class="bg-primary-500 h-2 rounded-full transition-all duration-300"
               :style="`width: ${surveyProgress}%`"></div>
        </div>
      </div>

      <!-- Survey Fields -->
      <div v-for="field in visibleSurveyFields" :key="field.id"
           class="rounded border border-gray-200 p-3">
        <SurveyFieldRenderer
          :field="field"
          :modelValue="localFormData[field.id]"
          @update:modelValue="updateField(field.id, $event)"
          :error="fieldErrors[field.id]"
          @validate="validateField(field)"
        />
      </div>
    </div>

    <!-- JSON Schema Form Rendering -->
    <div v-else-if="hasJsonSchemaConfiguration" class="space-y-2">
      <div v-for="(propSchema, propKey) in workType.configuration.properties" :key="propKey">

        <!-- Array of objects -->
        <div v-if="propSchema.type === 'array' && propSchema.items">
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <label class="block text-xs text-gray-500">
                {{ propSchema.title || propKey }}
                <span v-if="isFieldRequired(workType.configuration, propKey)" class="text-red-500">*</span>
              </label>
              <button
                @click="addArrayItem(propKey)"
                type="button"
                class="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
              >
                <FontAwesomeIcon :icon="['fas', 'plus']" class="mr-1" />
                Add Entry
              </button>
            </div>

            <!-- Array items -->
            <div v-if="localFormData[propKey] && localFormData[propKey].length > 0" class="space-y-2">
              <div v-for="(item, itemIndex) in localFormData[propKey]" :key="itemIndex"
                   class="p-2.5 border border-gray-200 rounded bg-gray-50">

                <div class="flex items-center justify-between mb-3">
                  <h5 class="text-xs text-gray-500">Entry {{ itemIndex + 1 }}</h5>
                  <button
                    @click="removeArrayItem(propKey, itemIndex)"
                    type="button"
                    class="text-red-600 hover:text-red-700"
                  >
                    <FontAwesomeIcon :icon="['fas', 'trash']" />
                  </button>
                </div>

                <!-- Array item fields -->
                <div class="space-y-2">
                  <div v-for="fieldKey in getFieldOrder(propSchema.items)" :key="fieldKey">
                    <div v-if="propSchema.items.properties[fieldKey]">
                      <label :for="`${propKey}_${itemIndex}_${fieldKey}`" class="block text-xs text-gray-500 mb-0.5">
                        {{ propSchema.items.properties[fieldKey].title || fieldKey }}
                        <span v-if="isFieldRequired(propSchema.items, fieldKey)" class="text-red-500">*</span>
                      </label>

                      <!-- Textarea -->
                      <textarea
                        v-if="propSchema.items.properties[fieldKey].format === 'textarea'"
                        :id="`${propKey}_${itemIndex}_${fieldKey}`"
                        :value="localFormData[propKey][itemIndex][fieldKey]"
                        @input="updateArrayField(propKey, itemIndex, fieldKey, $event.target.value)"
                        :required="isFieldRequired(propSchema.items, fieldKey)"
                        rows="3"
                        class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      ></textarea>

                      <!-- Select -->
                      <select
                        v-else-if="propSchema.items.properties[fieldKey].enum"
                        :id="`${propKey}_${itemIndex}_${fieldKey}`"
                        :value="localFormData[propKey][itemIndex][fieldKey]"
                        @change="updateArrayField(propKey, itemIndex, fieldKey, $event.target.value)"
                        :required="isFieldRequired(propSchema.items, fieldKey)"
                        class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">-- Select --</option>
                        <option v-for="option in propSchema.items.properties[fieldKey].enum" :key="option" :value="option">
                          {{ option }}
                        </option>
                      </select>

                      <!-- Text input -->
                      <input
                        v-else
                        :id="`${propKey}_${itemIndex}_${fieldKey}`"
                        :value="localFormData[propKey][itemIndex][fieldKey]"
                        @input="updateArrayField(propKey, itemIndex, fieldKey, $event.target.value)"
                        type="text"
                        :required="isFieldRequired(propSchema.items, fieldKey)"
                        class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-2">
              <p class="text-xs text-gray-400">No entries added yet. Click "Add Entry" to begin.</p>
            </div>
          </div>
        </div>

        <!-- Single field -->
        <div v-else>
          <label :for="propKey" class="block text-xs text-gray-500 mb-0.5">
            {{ propSchema.title || propKey }}
            <span v-if="isFieldRequired(workType.configuration, propKey)" class="text-red-500">*</span>
          </label>

          <!-- Textarea -->
          <textarea
            v-if="propSchema.format === 'textarea'"
            :id="propKey"
            :value="localFormData[propKey]"
            @input="updateField(propKey, $event.target.value)"
            :required="isFieldRequired(workType.configuration, propKey)"
            rows="3"
            class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          ></textarea>

          <!-- Select -->
          <select
            v-else-if="propSchema.enum"
            :id="propKey"
            :value="localFormData[propKey]"
            @change="updateField(propKey, $event.target.value)"
            :required="isFieldRequired(workType.configuration, propKey)"
            class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">-- Select --</option>
            <option v-for="option in propSchema.enum" :key="option" :value="option">
              {{ option }}
            </option>
          </select>

          <!-- Number -->
          <input
            v-else-if="propSchema.type === 'number' || propSchema.type === 'integer'"
            :id="propKey"
            :value="localFormData[propKey]"
            @input="updateField(propKey, Number($event.target.value))"
            type="number"
            :required="isFieldRequired(workType.configuration, propKey)"
            class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />

          <!-- Text -->
          <input
            v-else
            :id="propKey"
            :value="localFormData[propKey]"
            @input="updateField(propKey, $event.target.value)"
            type="text"
            :required="isFieldRequired(workType.configuration, propKey)"
            class="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>
    </div>

    <!-- No Configuration -->
    <div v-else class="text-center py-3">
      <p class="text-xs text-gray-400">No fields configured for this work type</p>
    </div>

    <!-- Media Upload Section -->
    <div v-if="currentUser" class="mt-2 border border-gray-200 rounded p-3">
      <h4 class="text-xs font-semibold text-gray-900 mb-3">Attachments</h4>

      <!-- Image Uploader -->
      <div v-if="workItem && workItem.id" class="mb-3">
        <ImageUploader
          :related-object-id="workItem.id"
          :related-object-content-type-model="'work'"
          :disabled="uploadingImage"
          @file-uploaded="addImage"
        />
      </div>
      <div v-else class="mb-3 text-xs text-gray-500 italic">
        Save the work item first to upload attachments
      </div>

      <!-- Display uploaded images -->
      <div v-if="uploadedImages && uploadedImages.length > 0" class="space-y-2">
        <div class="text-xs text-gray-500 font-medium">Uploaded Files:</div>
        <div v-for="image in uploadedImages"
             :key="image.key"
             class="flex items-center gap-2">
          <MediaViewer
            :can-delete="!uploadingImage"
            :media-key="image.key"
            :filename="image.filename"
            @media-deleted="removeImage"
          />
        </div>
      </div>
      <div v-else-if="!workItem || !workItem.id" class="text-xs text-gray-400">
        No attachments yet
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import SurveyFieldRenderer from '@/components/widgetcomponents/JSON_Editors/SurveyFieldRenderer.vue';
import ImageUploader from '@/components/widgetcomponents/WorkFileUploader.vue';
import MediaViewer from '@/components/widgetcomponents/MediaViewer.vue';
import axios from 'axios';

export default {
  name: 'GenericWorkForm',
  components: {
    FontAwesomeIcon,
    SurveyFieldRenderer,
    ImageUploader,
    MediaViewer
  },
  props: {
    workType: {
      type: Object,
      required: true
    },
    formData: {
      type: Object,
      default: () => ({})
    },
    workItem: {
      type: Object,
      default: null
    },
    currentUser: {
      type: Object,
      default: null
    }
  },
  emits: ['update:formData', 'validation-change'],
  data() {
    return {
      localFormData: {},
      fieldErrors: {},
      uploadingImage: false,
      uploadedImages: [],
      _formMountCount: 0,
      _formResetCount: 0,
      _lastInteractionAt: null,
    };
  },
  computed: {
    isSurveySchema() {
      return this.workType?.configuration?.schema_type === 'survey';
    },

    hasJsonSchemaConfiguration() {
      return !this.isSurveySchema &&
             this.workType?.configuration?.properties &&
             Object.keys(this.workType.configuration.properties).length > 0;
    },

    surveyFields() {
      if (!this.isSurveySchema) return [];
      const fields = this.workType.configuration.fields || [];
      return [...fields].sort((a, b) => (a.order || 0) - (b.order || 0));
    },

    visibleSurveyFields() {
      return this.surveyFields.filter(field => this.isFieldVisible(field));
    },

    requiredFieldsCount() {
      return this.visibleSurveyFields.filter(field => field.required).length;
    },

    completedFieldsCount() {
      return this.visibleSurveyFields.filter(field => {
        if (!field.required) return false;
        const value = this.localFormData[field.id];
        if (field.type === 'checkbox') {
          return Array.isArray(value) && value.length > 0;
        }
        return value !== undefined && value !== null && value !== '';
      }).length;
    },

    surveyProgress() {
      if (this.requiredFieldsCount === 0) return 100;
      return Math.round((this.completedFieldsCount / this.requiredFieldsCount) * 100);
    },

    isValid() {
      return this.validateForm();
    }
  },
  watch: {
    formData: {
      handler(newVal, oldVal) {
        if (this._formMountCount > 0 && this._lastInteractionAt) {
          this._formResetCount++;
          console.warn('[GenericWorkForm] formData prop reset after user interaction', {
            resetCount: this._formResetCount,
            lastInteraction: this._lastInteractionAt,
            newValKeys: Object.keys(newVal || {}),
            oldValKeys: Object.keys(oldVal || {}),
          });
        }
        this.localFormData = { ...newVal };
      },
      deep: true,
      immediate: true
    },
    localFormData: {
      handler(newVal) {
        this.$emit('update:formData', newVal);
        this.$emit('validation-change', this.validateForm());
      },
      deep: true
    }
  },
  mounted() {
    this._formMountCount++;
    this.initializeFormData();
  },
  methods: {
    initializeFormData() {
      if (this.isSurveySchema) {
        this.initializeSurveyFormData();
      } else if (this.hasJsonSchemaConfiguration) {
        this.initializeJsonSchemaFormData();
      }
    },

    initializeSurveyFormData() {
      const fields = this.workType.configuration.fields || [];
      const data = {};

      fields.forEach(field => {
        if (field.type === 'checkbox') {
          data[field.id] = this.localFormData[field.id] || [];
        } else if (field.type === 'number' || field.type === 'boolean' || field.type === 'scale') {
          data[field.id] = this.localFormData[field.id] ?? null;
        } else {
          data[field.id] = this.localFormData[field.id] || '';
        }
      });

      this.localFormData = { ...data };
    },

    initializeJsonSchemaFormData() {
      const config = this.workType.configuration;
      const data = {};

      Object.keys(config.properties).forEach(key => {
        const prop = config.properties[key];

        if (this.localFormData[key] !== undefined) {
          data[key] = this.localFormData[key];
        } else if (prop.type === 'array') {
          data[key] = [];
          if (config.required?.includes(key)) {
            this.addArrayItem(key);
          }
        } else {
          data[key] = this.getDefaultValue(prop);
        }
      });

      this.localFormData = { ...data };
    },

    getDefaultValue(schema) {
      if (schema.default !== undefined) return schema.default;
      if (schema.enum && schema.enum.length > 0) return '';
      if (schema.type === 'string') return '';
      if (schema.type === 'number' || schema.type === 'integer') return null;
      if (schema.type === 'boolean') return false;
      if (schema.type === 'array') return [];
      if (schema.type === 'object') return {};
      return '';
    },

    updateField(key, value) {
      this._lastInteractionAt = new Date().toISOString();
      this.localFormData = { ...this.localFormData, [key]: value };
    },

    updateArrayField(propKey, itemIndex, fieldKey, value) {
      const newData = { ...this.localFormData };
      if (!newData[propKey]) newData[propKey] = [];
      if (!newData[propKey][itemIndex]) newData[propKey][itemIndex] = {};
      newData[propKey][itemIndex][fieldKey] = value;
      this.localFormData = newData;
    },

    addArrayItem(propKey) {
      const newData = { ...this.localFormData };
      if (!newData[propKey]) newData[propKey] = [];

      const propSchema = this.workType.configuration.properties[propKey];
      if (propSchema?.items) {
        const newItem = this.createEmptyArrayItem(propSchema.items);
        newData[propKey].push(newItem);
      }
      this.localFormData = newData;
    },

    removeArrayItem(propKey, index) {
      const newData = { ...this.localFormData };
      if (newData[propKey] && Array.isArray(newData[propKey])) {
        newData[propKey].splice(index, 1);
      }
      this.localFormData = newData;
    },

    createEmptyArrayItem(itemSchema) {
      const item = {};
      if (itemSchema.properties) {
        Object.keys(itemSchema.properties).forEach(key => {
          item[key] = this.getDefaultValue(itemSchema.properties[key]);
        });
      }
      return item;
    },

    getFieldOrder(itemSchema) {
      if (itemSchema['ui:order'] && Array.isArray(itemSchema['ui:order'])) {
        return itemSchema['ui:order'];
      }
      return Object.keys(itemSchema.properties || {});
    },

    isFieldRequired(schema, fieldKey) {
      return schema.required && Array.isArray(schema.required) && schema.required.includes(fieldKey);
    },

    isFieldVisible(field) {
      if (!field.condition) return true;

      const condition = field.condition;
      const rules = condition.rules || [];

      if (rules.length === 0) return true;

      const results = rules.map(rule => this.evaluateConditionRule(rule));

      const meetsCondition = condition.logic === 'OR'
        ? results.some(r => r)
        : results.every(r => r);

      return condition.type === 'show_if' ? meetsCondition : !meetsCondition;
    },

    evaluateConditionRule(rule) {
      const fieldValue = this.localFormData[rule.field];
      const compareValue = rule.value;

      switch (rule.operator) {
        case 'equals':
          return fieldValue === compareValue;
        case 'not_equals':
          return fieldValue !== compareValue;
        case 'greater_than':
          return Number(fieldValue) > Number(compareValue);
        case 'less_than':
          return Number(fieldValue) < Number(compareValue);
        case 'contains':
          return String(fieldValue).includes(String(compareValue));
        case 'in':
          return Array.isArray(compareValue) && compareValue.includes(fieldValue);
        case 'not_empty':
          return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
        case 'is_empty':
          return fieldValue === null || fieldValue === undefined || fieldValue === '';
        default:
          return true;
      }
    },

    validateField(field) {
      delete this.fieldErrors[field.id];

      const value = this.localFormData[field.id];

      if (field.required && (value === null || value === undefined || value === '')) {
        this.fieldErrors[field.id] = 'This field is required';
        return false;
      }

      if (field.validation && value !== null && value !== undefined && value !== '') {
        const validation = field.validation;

        if (field.type === 'number') {
          const numValue = Number(value);
          if (validation.min !== undefined && numValue < validation.min) {
            this.fieldErrors[field.id] = validation.customMessage || `Value must be at least ${validation.min}`;
            return false;
          }
          if (validation.max !== undefined && numValue > validation.max) {
            this.fieldErrors[field.id] = validation.customMessage || `Value must be at most ${validation.max}`;
            return false;
          }
        }

        if (field.type === 'text' || field.type === 'textarea') {
          const strValue = String(value);
          if (validation.minLength !== undefined && strValue.length < validation.minLength) {
            this.fieldErrors[field.id] = validation.customMessage || `Must be at least ${validation.minLength} characters`;
            return false;
          }
          if (validation.maxLength !== undefined && strValue.length > validation.maxLength) {
            this.fieldErrors[field.id] = validation.customMessage || `Must be at most ${validation.maxLength} characters`;
            return false;
          }
          if (validation.pattern) {
            const regex = new RegExp(validation.pattern);
            if (!regex.test(strValue)) {
              this.fieldErrors[field.id] = validation.customMessage || 'Invalid format';
              return false;
            }
          }
        }
      }

      this.fieldErrors = { ...this.fieldErrors };
      return true;
    },

    validateForm() {
      if (this.isSurveySchema) {
        for (const field of this.visibleSurveyFields) {
          if (field.required) {
            const value = this.localFormData[field.id];
            if (field.type === 'checkbox') {
              if (!Array.isArray(value) || value.length === 0) return false;
            } else if (value === undefined || value === null || value === '') {
              return false;
            }
          }
        }
        return true;
      } else if (this.hasJsonSchemaConfiguration) {
        const config = this.workType.configuration;

        if (config.required) {
          for (const requiredField of config.required) {
            const value = this.localFormData[requiredField];

            if (Array.isArray(value)) {
              if (value.length === 0) return false;

              const propSchema = config.properties[requiredField];
              if (propSchema?.items?.required) {
                for (const item of value) {
                  for (const itemRequiredField of propSchema.items.required) {
                    if (!item[itemRequiredField] || item[itemRequiredField] === '') {
                      return false;
                    }
                  }
                }
              }
            } else {
              if (value === undefined || value === null || value === '') {
                return false;
              }
            }
          }
        }
        return true;
      }
      return true;
    },

    getFormData() {
      if (this.isSurveySchema) {
        const visibleFieldIds = this.visibleSurveyFields.map(f => f.id);
        const submissionData = {};

        for (const fieldId of visibleFieldIds) {
          const value = this.localFormData[fieldId];
          if (value !== undefined && value !== null && value !== '' &&
              !(Array.isArray(value) && value.length === 0)) {
            submissionData[fieldId] = value;
          }
        }

        submissionData._survey_metadata = {
          title: this.workType.configuration.title,
          version: this.workType.configuration.metadata?.version || '1.0.0',
          completed_fields: this.completedFieldsCount,
          total_required_fields: this.requiredFieldsCount
        };

        return submissionData;
      }

      return { ...this.localFormData };
    },

    async addImage(imageData) {
      if (!this.workItem || !this.workItem.id) {
        // If no work item exists yet, just store locally
        const newImage = {
          key: imageData.key,
          url: imageData.url,
          filename: imageData.filename,
          uploaded_by: {
            appuser_id: this.currentUser?.id,
            appuser_name: this.currentUser ? `${this.currentUser.last_name}, ${this.currentUser.first_name}` : 'Unknown User',
            created_at: new Date().toISOString()
          }
        };
        this.uploadedImages.push(newImage);
        return;
      }

      this.uploadingImage = true;

      try {
        const updatedImages = [...this.uploadedImages];

        const newImage = {
          key: imageData.key,
          url: imageData.url,
          filename: imageData.filename,
          uploaded_by: {
            appuser_id: this.currentUser?.id,
            appuser_name: this.currentUser ? `${this.currentUser.last_name}, ${this.currentUser.first_name}` : 'Unknown User',
            created_at: new Date().toISOString()
          }
        };

        updatedImages.push(newImage);
        this.uploadedImages = updatedImages;

        // If work item exists, update it
        await axios.patch(`/api/work/${this.workItem.id}/`, {
          metadata: {
            ...this.workItem.metadata,
            uploaded_images: updatedImages
          }
        });
      } catch (error) {
        console.error('Error adding image:', error);
        alert('Failed to add image. Please try again.');
      } finally {
        this.uploadingImage = false;
      }
    },

    async removeImage(deletedKey) {
      if (!this.workItem || !this.workItem.id) {
        // If no work item, just remove locally
        this.uploadedImages = this.uploadedImages.filter(img => img.key !== deletedKey);
        return;
      }

      this.uploadingImage = true;

      try {
        const updatedImages = this.uploadedImages.filter(img => img.key !== deletedKey);
        this.uploadedImages = updatedImages;

        await axios.patch(`/api/work/${this.workItem.id}/`, {
          metadata: {
            ...this.workItem.metadata,
            uploaded_images: updatedImages
          }
        });
      } catch (error) {
        console.error('Error removing image:', error);
        alert('Failed to remove image. Please try again.');
      } finally {
        this.uploadingImage = false;
      }
    },

    getUploadedImages() {
      // Include images from formData for saved work items
      return this.uploadedImages;
    }
  }
};
</script>
